<template>
  <div class="gx-about">
    <stars-canvas
      :count="100"
      :meteor-max="2"
      :meteor-gap="900"
      :star-colors="aboutStarColors"
      fixed
    />
    <float-pts :count="14" :colors="aboutPointColors" fixed />
    <glow-orb color="rgba(212,169,70,.4)" :size="560" x="15%" y="18%" :op="0.07" fixed />
    <glow-orb color="rgba(212,169,70,.22)" :size="460" x="84%" y="56%" :op="0.06" fixed />

    <new-ui-nav active="about" :is-logged-in="isLoggedIn" register-label="注册" compact-auth @home="goHome" @login="goLogin" @register="goRegister" @member="goMember" @about="goAbout" @enter="goEnter" />

    <div class="gx-about__content">
      <!-- Hero -->
      <div class="gx-about__hero">
        <div class="gx-about__halo"></div>
        <div class="gx-about__tree" :class="{ 'gx-about__tree--show': m }">🌳</div>
        <div class="gx-about__hero-copy" :class="{ 'gx-about__hero-copy--show': m }">
          <div class="gx-about__eyebrow">
            <span class="gx-about__eyebrow-dot"></span>
            <span>ABOUT STAR TREE</span>
          </div>
          <h1 class="gx-about__title">观星树 — AI智能店铺运营平台</h1>
          <p class="gx-about__slogan">致力店铺无人化，让运营更简单</p>
          <p class="gx-about__sub">用AI赋能店铺运营，让每个商家都能轻松管理</p>

          <div class="gx-about__stats">
            <div
              v-for="(s, i) in stats"
              :key="s.label"
              class="gx-about__stat"
              :style="{ borderRight: i < stats.length - 1 ? '1px solid rgba(212,169,70,.12)' : 'none' }"
            >
              <div class="gx-about__stat-num">{{ s.num }}</div>
              <div class="gx-about__stat-label">{{ s.label }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 成长里程碑 -->
      <div v-reveal class="gx-about__timeline-wrap">
        <section-head en="OUR JOURNEY" zh="成长里程碑" :visible="true" />
        <div class="gx-about__timeline">
          <div class="gx-about__tl-line"></div>

          <div
            v-for="(item, i) in timeline"
            :key="item.year"
            class="gx-about__tl-item"
            :class="{ 'gx-about__tl-item--future': item.year === '未来', 'gx-about__tl-item--left': i % 2 === 0 }"
            :style="{ '--gx-about-timeline-accent': item.accent }"
          >
            <div class="gx-about__tl-card" :class="{ 'gx-about__tl-card--future': item.year === '未来' }">
              <span v-if="item.year === '未来'" class="gx-about__tl-future-beam"></span>
              <span v-else class="gx-about__tl-beam"></span>
              <span v-if="item.year !== '未来'" class="gx-about__tl-dot">
                <span class="gx-about__tl-dot-icon">{{ item.icon }}</span>
              </span>
              <span v-if="item.year !== '未来'" class="gx-about__tl-num">0{{ i + 1 }}</span>
              <div v-if="item.year === '未来'" class="gx-about__tl-future-icon">{{ item.icon }}</div>
              <div v-if="item.year === '未来'" class="gx-about__tl-future-en">FUTURE · 未来</div>
              <div class="gx-about__tl-year" :class="{ 'gx-about__tl-year--future': item.year === '未来' }">{{ item.year }}</div>
              <div class="gx-about__tl-title" :class="{ 'gx-about__tl-title--future': item.year === '未来' }">{{ item.title }}</div>
              <div class="gx-about__tl-desc">{{ item.desc }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 我们的价值观 -->
      <div v-reveal class="gx-about__values">
        <section-head en="OUR VALUES" zh="我们的价值观" ac="#00C8F5" :visible="true" />
        <div class="gx-about__values-grid">
          <div
            v-for="(val, i) in values"
            :key="val.title"
            class="gx-about__value"
            :class="{ 'gx-about__value--hov': hovVal === i }"
            :style="valueStyle(val, i)"
            @mouseenter="hovVal = i"
            @mouseleave="hovVal = -1"
          >
            <span class="gx-about__value-beam" :style="valueBeamStyle(val)"></span>
            <span class="gx-about__value-ambient" :style="valueAmbientStyle(val)"></span>
            <span class="gx-about__value-p1" :style="valueParticleStyle(val, true)"></span>
            <span class="gx-about__value-p2" :style="valueParticleStyle(val, false)"></span>
            <div class="gx-about__value-orb">
              <span class="gx-about__value-ring" :style="valueRingStyle(val)"></span>
              <span class="gx-about__value-ring2" :style="valueRing2Style(val)"></span>
              <span class="gx-about__value-icon" :style="valueIconStyle(val, i)" v-html="valueIcons[i]"></span>
            </div>
            <div class="gx-about__value-title" :style="{ color: hovVal === i ? '#F0EBE0' : 'rgba(220,215,240,.88)' }">{{ val.title }}</div>
            <div class="gx-about__value-desc">{{ val.desc }}</div>
          </div>
        </div>
      </div>

      <!-- 联系我们 -->
      <div v-reveal class="gx-about__contacts">
        <section-head en="CONTACT US" zh="联系我们" ac="#00C8F5" :visible="true" />
        <div class="gx-about__contacts-grid">
          <div
            v-for="(ct, i) in contacts"
            :key="ct.label"
            class="gx-about__contact"
            :class="{ 'gx-about__contact--hov': hovCt === i }"
            :style="contactStyle(ct, i)"
            @mouseenter="hovCt = i"
            @mouseleave="hovCt = -1"
          >
            <span class="gx-about__contact-beam" :style="contactBeamStyle(ct)"></span>
            <span class="gx-about__contact-ambient" :style="contactAmbientStyle(ct)"></span>
            <div class="gx-about__contact-orb">
              <span class="gx-about__contact-ring" :style="contactRingStyle(ct)"></span>
              <span class="gx-about__contact-ring2" :style="contactRing2Style(ct)"></span>
              <span class="gx-about__contact-icon" :style="contactIconStyle(ct, i)" v-html="contactIcons[i]"></span>
            </div>
            <div class="gx-about__contact-en" :style="{ color: hovCt === i ? ct.color : '#8A95B0' }">{{ ct.label.toUpperCase() }}</div>
            <div class="gx-about__contact-val" :style="contactValStyle(ct)">{{ ct.val }}</div>
            <div class="gx-about__contact-sub" :style="{ color: hovCt === i ? ct.color + '99' : '#8A95B0' }">{{ ct.sub }}</div>
          </div>
        </div>
      </div>

      <!-- 底部 CTA -->
      <div v-reveal class="gx-about__cta">
        <div class="gx-about__cta-beam"></div>
        <div class="gx-about__cta-tree">🌳</div>
        <h2 class="gx-about__cta-title">加入观星树星人大家庭</h2>
        <p class="gx-about__cta-sub">与10,000+星人一起，让AI赋能您的电商之路</p>
        <div class="gx-about__cta-btns">
          <button class="gx-about__cta-primary" @click="isLoggedIn ? goEnter() : goRegister()">{{ isLoggedIn ? '进入工作台' : '免费注册' }}</button>
          <button class="gx-about__cta-ghost" @click="goMember">查看会员权益</button>
        </div>
      </div>
    </div>

    <div class="gx-about__bottom">
      <p>星观（杭州）数据科技有限公司 &nbsp;©&nbsp; 2026 &nbsp;·&nbsp; 浙ICP备2021028918号</p>
    </div>
  </div>
</template>

<script>
import StarsCanvas from '../../components/newUi/StarsCanvas.vue'
import FloatPts from '../../components/newUi/FloatPts.vue'
import GlowOrb from '../../components/newUi/GlowOrb.vue'
import SectionHead from '../../components/newUi/SectionHead.vue'
import NewUiNav from '../../components/newUi/NewUiNav.vue'

const VALUE_ICONS = [
  '<svg width="28" height="28" viewBox="0 0 28 28" fill="none"><path d="M17 3L5 16h10l-2 9 15-14h-9l2-8z" fill="rgba(245,158,11,.75)" stroke="rgba(245,158,11,.5)" stroke-width=".8" stroke-linejoin="round"/></svg>',
  '<svg width="28" height="28" viewBox="0 0 28 28" fill="none"><path d="M14 4a8 8 0 014.6 14.5l-.6 3.5H10l-.6-3.5A8 8 0 0114 4z" stroke="#D4A946" stroke-width="1.7" fill="none"/><path d="M11 22h6M12 24.5h4" stroke="#D4A946" stroke-width="1.6" stroke-linecap="round"/><circle cx="14" cy="11" r="2.5" fill="#D4A946" opacity=".45"/></svg>',
  '<svg width="28" height="28" viewBox="0 0 28 28" fill="none"><circle cx="10" cy="9" r="3.5" stroke="#00C8F5" stroke-width="1.7"/><circle cx="18" cy="9" r="3.5" stroke="#00C8F5" stroke-width="1.7" opacity=".65"/><path d="M3 23c0-3.9 3.1-7 7-7s7 3.1 7 7" stroke="#00C8F5" stroke-width="1.7" stroke-linecap="round"/><path d="M20 16.5c2.2.9 4 3.2 4 6" stroke="#00C8F5" stroke-width="1.6" stroke-linecap="round" opacity=".55"/></svg>',
  '<svg width="28" height="28" viewBox="0 0 28 28" fill="none"><path d="M14 3L5 7v7.5c0 5.5 3.8 10.2 9 11.5 5.2-1.3 9-6 9-11.5V7L14 3z" stroke="#4CAF7A" stroke-width="1.8" stroke-linejoin="round" fill="none"/><path d="M9.5 14.5l3.5 3.5 6.5-7" stroke="#4CAF7A" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>'
]

const CONTACT_ICONS = [
  '<svg width="32" height="32" viewBox="0 0 32 32" fill="none"><rect x="3" y="5" width="17" height="13" rx="4" stroke="#D4A946" stroke-width="1.8"/><path d="M5.5 18.5v3.5l4.5-3.5" stroke="#D4A946" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/><circle cx="9.5" cy="12" r="1.5" fill="#D4A946"/><circle cx="14" cy="12" r="1.5" fill="#D4A946"/><path d="M17 15h7a3 3 0 013 3v5a3 3 0 01-3 3H22l-3 2v-2h-2a3 3 0 01-3-3v-2" stroke="#D4A946" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" opacity=".65"/></svg>',
  '<svg width="32" height="32" viewBox="0 0 32 32" fill="none"><rect x="3" y="8" width="26" height="17" rx="4" stroke="#00C8F5" stroke-width="1.8"/><path d="M3 12l13 9 13-9" stroke="#00C8F5" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>'
]

export default {
  name: 'NewAboutPage',
  components: { StarsCanvas, FloatPts, GlowOrb, SectionHead, NewUiNav },
  data() {
    return {
      m: false,
      hovVal: -1,
      hovCt: -1,
      valueIcons: VALUE_ICONS,
      contactIcons: CONTACT_ICONS,
      aboutStarColors: ['#ffffff', '#ffffff', '#f0f4ff', '#d4a946', '#e8c778'],
      aboutPointColors: ['#d4a946', '#f0c44a', '#e8c778'],
      stats: [
        { num: '20,000+', label: '服务店铺' },
        { num: '100+', label: '覆盖类目' },
        { num: '3,000万+', label: '累计投入' },
        { num: '2021', label: '年创立' }
      ],
      timeline: [
        { year: '2021', icon: '🌱', title: '播下种子', desc: '星观（杭州）数据科技有限公司在杭州创立，以AI赋能中小电商创业者为使命，一颗种子悄然落地。', accent: '#4CAF7A' },
        { year: '2022', icon: '🌿', title: '破土生芽', desc: '投入1000万，观星树（数据平台）和话圈（交流平台）相继上线，首批星人用户加入，开始扎根成长。', accent: '#D4A946' },
        { year: '2023', icon: '🍂', title: '项目失败', desc: '观星树和话圈相继关停业务，公司业务巨大调整，追加投资1000万，星好货（供应链平台），星检测（AI检测商品违规平台）启动。', accent: '#C86A5B' },
        { year: '2024', icon: '🌱', title: '再次发芽', desc: '星检测（AI检测商品违规平台）2024年4月上线，星好货（供应链平台）2024年11月上线，大量星人用户涌入。', accent: '#00C8F5' },
        { year: '2025', icon: '🌳', title: '茁壮成长', desc: '星好货（供应链平台）2025年8月业务调整关停供应链，9月升级业务模型AI自动化运营，投资追加1000万。', accent: '#4CAF7A' },
        { year: '2026', icon: '🌲', title: '枝繁叶茂', desc: 'AI智能店铺运营全面升级，星群控2026年6月上线，星检测2026年7月新版上线，2026年8月星好货改名为观星树，服务店铺数量突破20000+，覆盖100+类目。', accent: '#D4A946' },
        { year: '未来', icon: '⭐', title: '星辰大海', desc: '全新AI引擎发布，助力10万+店铺迈向无人化运营，向星辰大海进发。', accent: '#D4A946' }
      ],
      values: [
        { title: '技术驱动', desc: '持续投入AI研发，让技术真正赋能每位电商创业者，降低运营门槛。', accent: 'rgba(245,158,11,.8)' },
        { title: '极致简单', desc: '复杂的AI，极简的交互，任何人都能轻松上手，5分钟启动运营。', accent: 'rgba(212,169,70,.8)' },
        { title: '共同成长', desc: '与星人用户一同进化，每一条反馈都是我们前进的动力与方向。', accent: 'rgba(0,200,245,.8)' },
        { title: '安全合规', desc: '守护店铺安全是底线，合规运营是长久之道，四维检测全天候守护。', accent: 'rgba(76,175,122,.8)' }
      ],
      contacts: [
        { label: '微信客服', val: '扫码添加', sub: '工作日 9:00-18:00', color: '#D4A946', bg: 'rgba(212,169,70,.04)' },
        { label: '邮箱', val: 'support@startree.ai', sub: '24h内回复', color: '#00C8F5', bg: 'rgba(0,200,245,.04)' }
      ]
    }
  },
  computed: {
    isLoggedIn() {
      return !!this.$store.state.login.token
    }
  },
  mounted() {
    setTimeout(() => { this.m = true }, 80)
  },
  methods: {
    accentSolid(accent) {
      return accent.replace(/,[^,)]+\)$/, ',1)')
    },
    valueStyle(val, i) {
      const hov = this.hovVal === i
      const ac = this.accentSolid(val.accent)
      return {
        background: hov ? `linear-gradient(160deg,${ac}0A 0%,rgba(13,22,48,.85) 100%)` : 'rgba(255,255,255,.04)',
        border: `1px solid ${hov ? ac + '45' : 'rgba(212,169,70,.12)'}`,
        boxShadow: hov ? `0 20px 56px rgba(0,0,0,.5), 0 0 40px ${ac}18, inset 0 1px rgba(255,255,255,.08)` : '0 4px 24px rgba(0,0,0,.28), inset 0 1px rgba(255,255,255,.05)',
        transform: hov ? 'translateY(-10px)' : 'none',
        transition: `all .5s cubic-bezier(.22,1,.36,1) ${i * 0.1}s`
      }
    },
    valueBeamStyle(val) {
      return { background: `linear-gradient(90deg,transparent,${this.accentSolid(val.accent)},transparent)` }
    },
    valueAmbientStyle(val) {
      const ac = this.accentSolid(val.accent)
      return { background: `radial-gradient(ellipse at 50% 0%,${ac}${this.hovVal === this.values.indexOf(val) ? '14' : '07'} 0%,transparent 65%)` }
    },
    valueParticleStyle(val, first) {
      const ac = this.accentSolid(val.accent)
      const hov = this.hovVal === this.values.indexOf(val)
      return {
        background: ac,
        boxShadow: `0 0 5px ${ac}`,
        top: first ? '14px' : '22px',
        right: first ? '16px' : '24px',
        width: first ? '2.5px' : '1.5px',
        height: first ? '2.5px' : '1.5px',
        opacity: hov ? (first ? 0.6 : 0.35) : (first ? 0.15 : 0.08)
      }
    },
    valueRingStyle(val) {
      const ac = this.accentSolid(val.accent)
      return { border: `1px solid ${ac}25`, borderTop: `1px solid ${ac}80` }
    },
    valueRing2Style(val) {
      const ac = this.accentSolid(val.accent)
      return { border: `1px dashed ${ac}18` }
    },
    valueIconStyle(val, i) {
      const hov = this.hovVal === i
      const ac = this.accentSolid(val.accent)
      return {
        background: hov ? `radial-gradient(circle,${ac}1E 0%,${ac}08 100%)` : `${ac}0D`,
        border: `1.5px solid ${ac}38`,
        boxShadow: hov ? `0 0 28px ${ac}50, inset 0 0 14px ${ac}12` : `0 0 14px ${ac}20`
      }
    },
    contactStyle(ct, i) {
      const hov = this.hovCt === i
      return {
        background: hov ? `linear-gradient(160deg,${ct.color}0E 0%,rgba(13,22,48,.88) 100%)` : ct.bg,
        border: `1px solid ${hov ? ct.color + '55' : 'rgba(255,255,255,.08)'}`,
        boxShadow: hov ? `0 24px 64px rgba(0,0,0,.55), 0 0 50px ${ct.color}18, inset 0 1px rgba(255,255,255,.07)` : '0 6px 28px rgba(0,0,0,.3), inset 0 1px rgba(255,255,255,.04)',
        transform: hov ? 'translateY(-10px)' : 'none',
        transition: `all .5s cubic-bezier(.22,1,.36,1) ${i * 0.14}s`
      }
    },
    contactBeamStyle(ct) {
      return { background: `linear-gradient(90deg,transparent,${ct.color},transparent)` }
    },
    contactAmbientStyle(ct) {
      return { background: `radial-gradient(ellipse at 50% 0%,${ct.color}${this.hovCt === this.contacts.indexOf(ct) ? '14' : '07'} 0%,transparent 65%)` }
    },
    contactRingStyle(ct) {
      return { border: `1px solid ${ct.color}28`, borderTop: `1px solid ${ct.color}88` }
    },
    contactRing2Style(ct) {
      return { border: `1px dashed ${ct.color}20` }
    },
    contactIconStyle(ct, i) {
      const hov = this.hovCt === i
      return {
        background: hov ? `radial-gradient(circle,${ct.color}20 0%,${ct.color}08 100%)` : `${ct.color}0C`,
        border: `1.5px solid ${ct.color}40`,
        boxShadow: hov ? `0 0 36px ${ct.color}55, inset 0 0 18px ${ct.color}12` : `0 0 16px ${ct.color}25`
      }
    },
    contactValStyle(ct) {
      const hov = this.hovCt === this.contacts.indexOf(ct)
      return {
        background: `linear-gradient(135deg,#F0EBE0,${ct.color})`,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        filter: hov ? `drop-shadow(0 0 8px ${ct.color}70)` : 'none'
      }
    },
    goHome() { this.$router.push('/') },
    goLogin() { this.$router.push('/login') },
    goRegister() { this.$router.push('/register') },
    goEnter() {
      if (this.isLoggedIn) this.$router.push('/ucenter?menuId=113')
      else this.$router.push('/login')
    },
    goMember() { this.$router.push('/vip') },
    goAbout() { this.$router.push('/about') }
  }
}
</script>

<style scoped>
.gx-about {
  --gx-about-bg-0: #070e22;
  --gx-about-bg-1: #0d1630;
  --gx-about-bg-2: #152248;
  --gx-about-text: #f0ebe0;
  --gx-about-muted: #8a95b0;
  --gx-about-accent: #d4a946;
  --gx-about-accent-light: #f0c44a;
  --gx-about-info: #00c8f5;
  --gx-about-success: #4caf7a;
  --gx-about-font-sans: "Noto Sans SC", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  --gx-about-font-display: "Noto Serif SC", Georgia, serif;
  --gx-about-font-mono: "JetBrains Mono", "SFMono-Regular", Consolas, monospace;
  --gx-about-ease: cubic-bezier(.22, 1, .36, 1);
  --gx-about-motion: .5s;
  min-height: 100vh;
  background: linear-gradient(180deg, var(--gx-about-bg-0) 0%, var(--gx-about-bg-1) 22%, var(--gx-about-bg-2) 55%, var(--gx-about-bg-1) 82%, var(--gx-about-bg-0) 100%);
  overflow-x: hidden;
  position: relative;
  color: var(--gx-about-text);
  font-family: var(--gx-about-font-sans);
}
.gx-about,
.gx-about * {
  font-family: var(--gx-about-font-sans);
}
.gx-about__content {
  padding-top: 64px;
  position: relative;
  z-index: 2;
}
.gx-about__hero {
  text-align: center;
  padding: 96px 24px 84px;
  position: relative;
}
.gx-about__halo {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 500px;
  height: 500px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(212, 169, 70, .07) 0%, transparent 65%);
  pointer-events: none;
}
.gx-about__tree {
  display: inline-block;
  font-size: 88px;
  margin-bottom: 22px;
  opacity: 0;
  transform: scale(.82);
  animation: gx-treeBreathe 3.5s ease-in-out infinite, gx-goldGlow 4s ease-in-out infinite;
  transition: opacity 1s ease, transform 1s var(--gx-about-ease);
}
.gx-about__tree--show {
  opacity: 1;
  transform: scale(1);
}
.gx-about__hero-copy {
  opacity: 0;
  transform: translateY(22px);
  transition: opacity .85s ease .12s, transform .85s var(--gx-about-ease) .12s;
}
.gx-about__hero-copy--show {
  opacity: 1;
  transform: none;
}
.gx-about__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 5px 18px;
  border-radius: 20px;
  border: 1px solid rgba(0, 200, 245, .3);
  background: rgba(0, 200, 245, .05);
  margin-bottom: 22px;
  font-family: var(--gx-about-font-mono);
  font-size: 10px;
  color: var(--gx-about-info);
  letter-spacing: 4px;
  font-weight: 600;
}
.gx-about__eyebrow-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--gx-about-info);
  animation: gx-pulse 2s infinite;
}
.gx-about__title {
  font-family: var(--gx-about-font-display);
  font-size: clamp(28px, 5vw, 52px);
  font-weight: 700;
  margin: 0 0 16px;
  line-height: 1.2;
  letter-spacing: .03em;
  background: linear-gradient(135deg, #F0EBE0 0%, #D4A946 40%, #F0C44A 60%, #F0EBE0 100%);
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: gx-shimmer 5s linear infinite;
}
.gx-about__slogan {
  font-size: 18px;
  font-weight: 500;
  color: rgba(220, 215, 240, .8);
  margin: 0 0 10px;
  letter-spacing: .04em;
}
.gx-about__sub {
  font-family: var(--gx-about-font-display);
  font-size: 15px;
  margin: 0 0 52px;
  background: linear-gradient(135deg, #D4A946, #00C8F5);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  letter-spacing: .03em;
}
.gx-about__stats {
  display: inline-grid;
  grid-template-columns: repeat(4, auto);
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid rgba(212, 169, 70, .18);
  background: rgba(255, 255, 255, .04);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  box-shadow: inset 0 1px rgba(255, 255, 255, .06);
}
.gx-about__stat {
  padding: 20px 36px;
  text-align: center;
}
.gx-about__stat-num {
  font-family: var(--gx-about-font-mono);
  font-size: 22px;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 6px;
  background: linear-gradient(135deg, #F0EBE0, #D4A946);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: gx-shimmer 5s linear infinite;
}
.gx-about__stat-label {
  font-size: 12px;
  color: #8A95B0;
  letter-spacing: .05em;
  opacity: .7;
}
.gx-about__timeline-wrap {
  max-width: 1040px;
  margin: 0 auto;
  padding: 0 32px 120px;
}
.gx-about__timeline {
  position: relative;
}
.gx-about__tl-line {
  position: absolute;
  left: 50%;
  top: 0;
  bottom: 0;
  width: 1.5px;
  transform: translateX(-50%);
  background: linear-gradient(180deg, transparent 0%, rgba(212, 169, 70, .33) 8%, rgba(212, 169, 70, .33) 92%, transparent 100%);
  transform-origin: top center;
  animation: gx-lineGrow .9s ease-out forwards;
}
.gx-about__tl-item {
  display: flex;
  justify-content: flex-end;
  padding-bottom: 52px;
  position: relative;
}
.gx-about__tl-item--left {
  justify-content: flex-start;
}
.gx-about__tl-item--future {
  justify-content: center;
  padding-top: 16px;
  padding-bottom: 0;
}
.gx-about__tl-card {
  width: 41%;
  padding: 26px 30px;
  border-radius: 20px;
  position: relative;
  background: rgba(255, 255, 255, .04);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: 1px solid rgba(212, 169, 70, .18);
  box-shadow: 0 8px 36px rgba(0, 0, 0, .4), inset 0 1px rgba(255, 255, 255, .06);
  transition: transform var(--gx-about-motion) var(--gx-about-ease), border-color var(--gx-about-motion) var(--gx-about-ease), box-shadow var(--gx-about-motion) var(--gx-about-ease);
}
.gx-about__tl-card:hover {
  transform: translateY(-6px);
  border-color: var(--gx-about-timeline-accent, var(--gx-about-accent));
  box-shadow: 0 16px 44px rgba(0, 0, 0, .48), inset 0 1px rgba(255, 255, 255, .08);
}
.gx-about__tl-item--future .gx-about__tl-card {
  width: 58%;
  padding: 30px 36px;
  border-radius: 22px;
  text-align: center;
  background: linear-gradient(135deg, rgba(212, 169, 70, .1) 0%, rgba(212, 169, 70, .04) 60%, rgba(255, 255, 255, .03) 100%);
  border-color: rgba(212, 169, 70, .45);
  box-shadow: 0 0 60px rgba(212, 169, 70, .12), 0 16px 48px rgba(0, 0, 0, .5), inset 0 1px rgba(255, 255, 255, .07);
}
.gx-about__tl-beam {
  position: absolute;
  top: 0;
  left: 15%;
  right: 15%;
  height: 1px;
  border-radius: 1px;
  background: linear-gradient(90deg, transparent, var(--gx-about-timeline-accent, var(--gx-about-accent)), transparent);
  opacity: .5;
}
.gx-about__tl-future-beam {
  position: absolute;
  top: 0;
  left: 20%;
  right: 20%;
  height: 1.5px;
  border-radius: 1px;
  background: linear-gradient(90deg, transparent, #D4A946, transparent);
}
.gx-about__tl-dot {
  position: absolute;
  top: 24px;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: linear-gradient(135deg, #152248, #0D1630);
  border: 2px solid var(--gx-about-timeline-accent, var(--gx-about-accent));
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  z-index: 2;
  box-shadow: 0 0 0 4px rgba(212, 169, 70, .07), 0 0 16px var(--gx-about-timeline-accent, var(--gx-about-accent));
  animation: gx-nodeGlow 3.5s ease-in-out infinite;
}
.gx-about__tl-item--left .gx-about__tl-dot {
  right: -26px;
  left: auto;
}
.gx-about__tl-item:not(.gx-about__tl-item--left) .gx-about__tl-dot {
  left: -26px;
  right: auto;
}
.gx-about__tl-num {
  position: absolute;
  top: 24px;
  font-family: var(--gx-about-font-mono);
  font-size: 10px;
  color: var(--gx-about-timeline-accent, var(--gx-about-accent));
  opacity: .42;
  letter-spacing: 1px;
  font-weight: 600;
}
.gx-about__tl-item--left .gx-about__tl-num {
  left: 24px;
  right: auto;
}
.gx-about__tl-item:not(.gx-about__tl-item--left) .gx-about__tl-num {
  right: 24px;
  left: auto;
}
.gx-about__tl-future-icon {
  font-size: 36px;
  margin-bottom: 12px;
  display: inline-block;
  animation: gx-starSpin 8s linear infinite;
}
.gx-about__tl-future-en {
  font-family: var(--gx-about-font-mono);
  font-size: 10px;
  color: #D4A946;
  letter-spacing: 4px;
  margin-bottom: 10px;
  font-weight: 600;
}
.gx-about__tl-year {
  font-family: var(--gx-about-font-mono);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 2px;
  margin-bottom: 8px;
  margin-top: 4px;
  background: linear-gradient(135deg, var(--gx-about-accent-light), var(--gx-about-timeline-accent, var(--gx-about-accent)));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}
.gx-about__tl-year--future {
  font-size: 12px;
}
.gx-about__tl-title {
  font-family: var(--gx-about-font-display);
  font-size: 18px;
  font-weight: 700;
  color: #F0EBE0;
  margin-bottom: 10px;
  letter-spacing: .02em;
}
.gx-about__tl-title--future {
  font-size: 22px;
  background: linear-gradient(135deg, #F0EBE0, #D4A946);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: gx-shimmer 4s linear infinite;
}
.gx-about__tl-desc {
  font-size: 13.5px;
  color: rgba(220, 215, 240, .72);
  line-height: 1.9;
}
.gx-about__values {
  background: linear-gradient(180deg, rgba(255, 255, 255, .02) 0%, rgba(255, 255, 255, .04) 50%, rgba(255, 255, 255, .02) 100%);
  border-top: 1px solid rgba(212, 169, 70, .1);
  border-bottom: 1px solid rgba(212, 169, 70, .1);
  padding: 100px 52px;
}
.gx-about__values-grid {
  max-width: 1080px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 22px;
}
.gx-about__value {
  padding: 36px 24px;
  border-radius: 22px;
  text-align: center;
  cursor: default;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
}
.gx-about__value-beam {
  position: absolute;
  top: 0;
  left: 18%;
  right: 18%;
  height: 1.5px;
  border-radius: 1px;
  opacity: .3;
  transition: opacity .4s;
}
.gx-about__value--hov .gx-about__value-beam { opacity: 1; }
.gx-about__value-ambient {
  position: absolute;
  inset: 0;
  pointer-events: none;
}
.gx-about__value-p1,
.gx-about__value-p2 {
  position: absolute;
  border-radius: 50%;
}
.gx-about__value-orb {
  position: relative;
  width: 68px;
  height: 68px;
  margin: 0 auto 20px;
}
.gx-about__value-ring {
  position: absolute;
  inset: -3px;
  border-radius: 50%;
  animation: gx-orbSpin 7s linear infinite;
  opacity: .55;
  transition: opacity .4s;
}
.gx-about__value--hov .gx-about__value-ring { opacity: 1; }
.gx-about__value-ring2 {
  position: absolute;
  inset: 6px;
  border-radius: 50%;
  animation: gx-orbSpin 11s linear infinite reverse;
}
.gx-about__value-icon {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.gx-about__value-title {
  font-family: var(--gx-about-font-display);
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 10px;
  letter-spacing: .04em;
  transition: color .3s;
}
.gx-about__value-desc {
  font-size: 13.5px;
  color: rgba(220, 215, 240, .65);
  line-height: 1.9;
}
.gx-about__contacts {
  max-width: 900px;
  margin: 0 auto;
  padding: 100px 32px 100px;
}
.gx-about__contacts-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  max-width: 600px;
  margin: 0 auto;
}
.gx-about__contact {
  padding: 40px 30px;
  border-radius: 24px;
  text-align: center;
  cursor: default;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(28px);
  -webkit-backdrop-filter: blur(28px);
}
.gx-about__contact-beam {
  position: absolute;
  top: 0;
  left: 18%;
  right: 18%;
  height: 1.5px;
  border-radius: 1px;
  opacity: .4;
  transition: opacity .4s;
}
.gx-about__contact--hov .gx-about__contact-beam { opacity: 1; }
.gx-about__contact-ambient {
  position: absolute;
  inset: 0;
  pointer-events: none;
}
.gx-about__contact-orb {
  position: relative;
  width: 80px;
  height: 80px;
  margin: 0 auto 22px;
}
.gx-about__contact-ring {
  position: absolute;
  inset: -3px;
  border-radius: 50%;
  animation: gx-orbSpin 6s linear infinite;
  opacity: .6;
  transition: opacity .4s;
}
.gx-about__contact--hov .gx-about__contact-ring { opacity: 1; }
.gx-about__contact-ring2 {
  position: absolute;
  inset: 7px;
  border-radius: 50%;
  animation: gx-orbSpin 10s linear infinite reverse;
}
.gx-about__contact-icon {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.gx-about__contact-en {
  font-family: var(--gx-about-font-mono);
  font-size: 10px;
  font-weight: 600;
  margin-bottom: 8px;
  letter-spacing: 3px;
  opacity: .6;
  transition: all .3s;
}
.gx-about__contact--hov .gx-about__contact-en { opacity: 1; }
.gx-about__contact-val {
  font-size: 17px;
  font-weight: 700;
  margin-bottom: 8px;
}
.gx-about__contact-sub {
  font-size: 13px;
  opacity: .55;
  transition: all .3s;
}
.gx-about__contact--hov .gx-about__contact-sub { opacity: 1; }
.gx-about__cta {
  max-width: 680px;
  margin: 0 auto 80px;
  padding: 60px 48px;
  border-radius: 24px;
  text-align: center;
  background: linear-gradient(145deg, rgba(212, 169, 70, .07) 0%, rgba(255, 255, 255, .035) 60%, rgba(0, 200, 245, .05) 100%);
  backdrop-filter: blur(28px);
  -webkit-backdrop-filter: blur(28px);
  border: 1px solid rgba(212, 169, 70, .25);
  box-shadow: 0 0 80px rgba(212, 169, 70, .07), 0 28px 72px rgba(0, 0, 0, .5), inset 0 1px rgba(255, 255, 255, .06);
  position: relative;
  overflow: hidden;
}
.gx-about__cta-beam {
  position: absolute;
  top: 0;
  left: 20%;
  right: 20%;
  height: 1.5px;
  border-radius: 1px;
  background: linear-gradient(90deg, transparent, #D4A946, transparent);
}
.gx-about__cta-tree {
  font-size: 52px;
  margin-bottom: 18px;
  display: inline-block;
  animation: gx-treeBreathe 3s ease-in-out infinite, gx-goldGlow 4s ease-in-out infinite;
}
.gx-about__cta-title {
  font-family: var(--gx-about-font-display);
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 12px;
  background: linear-gradient(135deg, #F0EBE0, #D4A946);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: gx-shimmer 5s linear infinite;
}
.gx-about__cta-sub {
  font-size: 15px;
  color: rgba(220, 215, 240, .65);
  margin: 0 0 36px;
  line-height: 1.85;
}
.gx-about__cta-btns {
  display: flex;
  gap: 14px;
  justify-content: center;
  flex-wrap: wrap;
}
.gx-about__cta-primary {
  height: 50px;
  padding: 0 40px;
  border-radius: 13px;
  cursor: pointer;
  background: linear-gradient(135deg, #D4A946, #F0C44A);
  border: none;
  font-family: var(--gx-about-font-display);
  font-size: 15px;
  font-weight: 700;
  color: #070E22;
  box-shadow: 0 6px 24px rgba(212, 169, 70, .5);
  transition: all 0.5s cubic-bezier(.22, 1, .36, 1);
}
.gx-about__cta-primary:hover {
  transform: scale(1.05);
  box-shadow: 0 10px 36px rgba(212, 169, 70, .7);
}
.gx-about__cta-ghost {
  height: 50px;
  padding: 0 40px;
  border-radius: 13px;
  cursor: pointer;
  background: transparent;
  border: 1.5px solid rgba(212, 169, 70, .38);
  font-family: var(--gx-about-font-display);
  font-size: 15px;
  color: #D4A946;
  transition: all 0.5s cubic-bezier(.22, 1, .36, 1);
}
.gx-about__cta-ghost:hover {
  background: rgba(212, 169, 70, .1);
}
.gx-about__bottom {
  background: rgba(4, 8, 22, .95);
  border-top: 1px solid rgba(212, 169, 70, .1);
  padding: 18px 52px;
  text-align: center;
  position: relative;
  z-index: 2;
}
.gx-about__bottom p {
  font-size: 13px;
  color: rgba(138, 149, 176, .35);
  margin: 0;
  letter-spacing: .04em;
}
@media (max-width: 900px) {
  .gx-about__stats {
    grid-template-columns: repeat(2, auto);
  }
  .gx-about__tl-item,
  .gx-about__tl-item--left {
    justify-content: center;
  }
  .gx-about__tl-card {
    width: 90%;
  }
  .gx-about__tl-line {
    display: none;
  }
  .gx-about__tl-dot,
  .gx-about__tl-num {
    display: none;
  }
  .gx-about__values-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .gx-about__values {
    padding: 60px 24px;
  }
  .gx-about__contacts-grid {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 700px) {
  .gx-about__content {
    padding-top: 58px;
  }
  .gx-about__hero {
    padding: 72px 16px 62px;
  }
  .gx-about__tree {
    font-size: 64px;
    margin-bottom: 16px;
  }
  .gx-about__eyebrow {
    padding: 5px 13px;
    font-size: 8px;
    letter-spacing: 3px;
  }
  .gx-about__title {
    font-size: clamp(30px, 10vw, 42px);
  }
  .gx-about__slogan {
    font-size: 15px;
  }
  .gx-about__sub {
    margin-bottom: 34px;
    font-size: 13px;
  }
  .gx-about__stats {
    width: min(100%, 360px);
    grid-template-columns: repeat(2, 1fr);
  }
  .gx-about__stat {
    padding: 16px 10px;
  }
  .gx-about__stat:nth-child(2) {
    border-right: 0 !important;
  }
  .gx-about__stat:nth-child(-n + 2) {
    border-bottom: 1px solid rgba(212, 169, 70, .12);
  }
  .gx-about__stat-num {
    font-size: 18px;
  }
  .gx-about__timeline-wrap,
  .gx-about__contacts {
    padding-right: 16px;
    padding-left: 16px;
  }
  .gx-about__timeline-wrap {
    padding-bottom: 74px;
  }
  .gx-about__tl-item,
  .gx-about__tl-item--left {
    padding-bottom: 22px;
  }
  .gx-about__tl-card,
  .gx-about__tl-item--future .gx-about__tl-card {
    width: 100%;
    padding: 22px 20px;
  }
  .gx-about__values {
    padding: 72px 16px;
  }
  .gx-about__values-grid {
    gap: 12px;
  }
  .gx-about__value {
    padding: 26px 16px;
  }
  .gx-about__value-orb {
    width: 58px;
    height: 58px;
    margin-bottom: 14px;
  }
  .gx-about__value-title {
    font-size: 15px;
  }
  .gx-about__value-desc,
  .gx-about__tl-desc {
    font-size: 12px;
    line-height: 1.75;
  }
  .gx-about__contacts {
    padding-top: 72px;
    padding-bottom: 72px;
  }
  .gx-about__contact {
    padding: 30px 22px;
  }
  .gx-about__cta {
    margin: 0 16px 56px;
    padding: 44px 22px;
  }
  .gx-about__cta-title {
    font-size: 24px;
  }
  .gx-about__cta-primary,
  .gx-about__cta-ghost {
    width: 100%;
  }
  .gx-about__bottom {
    padding: 16px;
  }
}
@media (prefers-reduced-motion: reduce) {
  .gx-about *,
  .gx-about *::before,
  .gx-about *::after {
    animation-duration: .01ms !important;
    animation-iteration-count: 1 !important;
    scroll-behavior: auto !important;
    transition-duration: .01ms !important;
  }
}
</style>
